import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { SuperAdminLayout } from './SuperAdminLayout';
import {
  Building2, Plus, Search, Filter, Eye, Pause, Play, Trash2,
  X, Users, CreditCard, MapPin, Phone, Mail, ChevronDown, Send
} from 'lucide-react';

const STATUS_MAP = {
  active: { key: 'saCompanies.status_active', cls: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/20' },
  suspended: { key: 'saCompanies.status_suspended', cls: 'bg-red-500/15 text-red-400 border-red-500/20' },
  overdue: { key: 'saCompanies.status_overdue', cls: 'bg-amber-500/15 text-amber-400 border-amber-500/20' },
  archived: { key: 'saCompanies.status_archived', cls: 'bg-gray-500/15 text-gray-400 border-gray-500/20' },
};

const PLAN_COLORS = {
  start: 'text-blue-400',
  pro: 'text-[#fedd00]',
  super: 'text-[#00d946]',
};

const SuperAdminCompanies = () => {
  const { api } = useAuth();
  const { t, formatCurrency, formatDate, formatNumber } = useLanguage();
  const [companies, setCompanies] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [planFilter, setPlanFilter] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [formData, setFormData] = useState({
    name: '', email: '', phone: '', address: '', contact_name: '', siret: '',
    subscription_plan: 'start', shop_count: 1, admin_email: '', admin_password: '', notes: ''
  });

  const fetchCompanies = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      if (statusFilter) params.append('status', statusFilter);
      if (planFilter) params.append('plan', planFilter);
      const res = await api.get(`/super-admin/companies?${params}`);
      setCompanies(res.data.companies);
      setTotal(res.data.total);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [api, search, statusFilter, planFilter]);

  useEffect(() => { fetchCompanies(); }, [fetchCompanies]);

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      await api.post('/super-admin/companies', formData);
      setShowCreate(false);
      setFormData({ name: '', email: '', phone: '', address: '', contact_name: '', siret: '', subscription_plan: 'start', shop_count: 1, admin_email: '', admin_password: '', notes: '' });
      fetchCompanies();
    } catch (err) {
      alert(err.response?.data?.detail || t('saCompanies.error'));
    }
  };

  const handleSuspend = async (id) => {
    if (!window.confirm(t('saCompanies.confirm_suspend'))) return;
    await api.post(`/super-admin/companies/${id}/suspend`, { reason: t('saCompanies.suspend_reason_manual') });
    fetchCompanies();
  };

  const handleActivate = async (id) => {
    await api.post(`/super-admin/companies/${id}/activate`);
    fetchCompanies();
  };

  const handleArchive = async (id) => {
    if (!window.confirm(t('saCompanies.confirm_archive'))) return;
    await api.delete(`/super-admin/companies/${id}`);
    fetchCompanies();
  };

  const handleSendReminder = async (id) => {
    try {
      const res = await api.post(`/stripe/send-payment-reminder/${id}`);
      alert(res.data.message || t('saCompanies.reminder_sent'));
    } catch (err) {
      alert(err.response?.data?.detail || t('saCompanies.reminder_error'));
    }
  };

  const fetchDetail = async (id) => {
    try {
      const res = await api.get(`/super-admin/companies/${id}`);
      setSelectedCompany(res.data);
    } catch (err) { console.error(err); }
  };

  return (
    <SuperAdminLayout>
      <div className="space-y-4" data-testid="super-admin-companies">
        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-xl font-bold text-white">{t('saCompanies.title')}</h1>
            <p className="text-sm text-gray-500">{t('saCompanies.total_companies', { count: formatNumber(total) })}</p>
          </div>
          <button
            onClick={() => setShowCreate(true)}
            data-testid="create-company-btn"
            className="flex items-center gap-2 px-4 py-2.5 bg-[#009b3a] text-white rounded-lg text-sm font-medium hover:bg-[#00b344] transition-colors"
          >
            <Plus className="w-4 h-4" /> {t('saCompanies.new_company')}
          </button>
        </div>

        {/* Filters */}
        <div className="flex items-center gap-3 flex-wrap">
          <div className="relative flex-1 min-w-[200px] max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
            <input
              type="text"
              placeholder={t('saCompanies.search_placeholder')}
              value={search}
              onChange={e => setSearch(e.target.value)}
              data-testid="search-companies"
              className="w-full pl-10 pr-4 py-2.5 bg-[#111827] border border-white/10 rounded-lg text-sm text-white placeholder-gray-500 focus:outline-none focus:border-[#009b3a]/40"
            />
          </div>
          <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} data-testid="filter-status" className="bg-[#111827] border border-white/10 rounded-lg px-3 py-2.5 text-sm text-gray-300 focus:outline-none">
            <option value="">{t('saCompanies.filter_all_status')}</option>
            <option value="active">{t('saCompanies.status_active')}</option>
            <option value="suspended">{t('saCompanies.status_suspended')}</option>
            <option value="overdue">{t('saCompanies.filter_overdue')}</option>
            <option value="archived">{t('saCompanies.status_archived')}</option>
          </select>
          <select value={planFilter} onChange={e => setPlanFilter(e.target.value)} data-testid="filter-plan" className="bg-[#111827] border border-white/10 rounded-lg px-3 py-2.5 text-sm text-gray-300 focus:outline-none">
            <option value="">{t('saCompanies.filter_all_plans')}</option>
            <option value="start">Start</option>
            <option value="pro">Pro</option>
            <option value="super">Super</option>
          </select>
        </div>

        {/* Companies Table */}
        <div className="bg-[#111827] border border-white/5 rounded-xl overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center p-12">
              <div className="w-6 h-6 border-2 border-[#009b3a] border-t-transparent rounded-full animate-spin" />
            </div>
          ) : companies.length === 0 ? (
            <div className="p-12 text-center">
              <Building2 className="w-10 h-10 text-gray-600 mx-auto mb-3" />
              <p className="text-sm text-gray-500">{t('saCompanies.empty')}</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-white/5 text-gray-500 text-xs">
                    <th className="text-left px-4 py-3 font-medium">{t('saCompanies.col_company')}</th>
                    <th className="text-left px-4 py-3 font-medium">{t('saCompanies.col_plan')}</th>
                    <th className="text-left px-4 py-3 font-medium">{t('saCompanies.col_status')}</th>
                    <th className="text-left px-4 py-3 font-medium">{t('saCompanies.col_users')}</th>
                    <th className="text-left px-4 py-3 font-medium">{t('saCompanies.col_registered')}</th>
                    <th className="text-right px-4 py-3 font-medium">{t('saCompanies.col_actions')}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/5">
                  {companies.map(c => {
                    const status = STATUS_MAP[c.status] || STATUS_MAP.active;
                    return (
                      <tr key={c.id} className="hover:bg-white/[0.02] transition-colors" data-testid={`company-row-${c.id}`}>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-lg bg-[#009b3a]/10 flex items-center justify-center flex-shrink-0">
                              <Building2 className="w-4 h-4 text-[#009b3a]" />
                            </div>
                            <div>
                              <p className="text-white font-medium">{c.name}</p>
                              <p className="text-xs text-gray-500">{c.email}</p>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <span className={`text-xs font-semibold uppercase ${PLAN_COLORS[c.subscription_plan] || 'text-gray-400'}`}>
                            {c.subscription_plan}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <span className={`inline-flex px-2 py-0.5 rounded-full text-xs border ${status.cls}`}>
                            {t(status.key)}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-gray-400">{formatNumber(c.user_count || 0)}</td>
                        <td className="px-4 py-3 text-gray-500 text-xs">
                          {formatDate(c.created_at)}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center justify-end gap-1">
                            <button onClick={() => fetchDetail(c.id)} data-testid={`view-company-${c.id}`} className="p-1.5 rounded-lg hover:bg-white/5 text-gray-400 hover:text-white" title={t('saCompanies.action_view')}>
                              <Eye className="w-4 h-4" />
                            </button>
                            <button onClick={() => handleSendReminder(c.id)} data-testid={`reminder-${c.id}`} className="p-1.5 rounded-lg hover:bg-amber-500/10 text-gray-400 hover:text-amber-400" title={t('saCompanies.action_reminder')}>
                              <Send className="w-4 h-4" />
                            </button>
                            {c.status === 'active' ? (
                              <button onClick={() => handleSuspend(c.id)} className="p-1.5 rounded-lg hover:bg-red-500/10 text-gray-400 hover:text-red-400" title={t('saCompanies.action_suspend')}>
                                <Pause className="w-4 h-4" />
                              </button>
                            ) : c.status === 'suspended' ? (
                              <button onClick={() => handleActivate(c.id)} className="p-1.5 rounded-lg hover:bg-emerald-500/10 text-gray-400 hover:text-emerald-400" title={t('saCompanies.action_reactivate')}>
                                <Play className="w-4 h-4" />
                              </button>
                            ) : null}
                            <button onClick={() => handleArchive(c.id)} className="p-1.5 rounded-lg hover:bg-red-500/10 text-gray-400 hover:text-red-400" title={t('saCompanies.action_archive')}>
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Create Modal */}
      {showCreate && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4">
          <div className="bg-[#111827] border border-white/10 rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto" data-testid="create-company-modal">
            <div className="flex items-center justify-between px-5 py-4 border-b border-white/5">
              <h2 className="text-base font-bold text-white">{t('saCompanies.new_company')}</h2>
              <button onClick={() => setShowCreate(false)} className="p-1 rounded-lg hover:bg-white/5">
                <X className="w-5 h-5 text-gray-400" />
              </button>
            </div>
            <form onSubmit={handleCreate} className="p-5 space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2">
                  <label className="block text-xs text-gray-400 mb-1">{t('saCompanies.field_company_name')} *</label>
                  <input required value={formData.name} onChange={e => setFormData(p => ({...p, name: e.target.value}))} className="w-full px-3 py-2.5 bg-[#0a0f1c] border border-white/10 rounded-lg text-sm text-white focus:outline-none focus:border-[#009b3a]/40" data-testid="input-company-name" />
                </div>
                <div>
                  <label className="block text-xs text-gray-400 mb-1">{t('saCompanies.field_contact')}</label>
                  <input value={formData.contact_name} onChange={e => setFormData(p => ({...p, contact_name: e.target.value}))} className="w-full px-3 py-2.5 bg-[#0a0f1c] border border-white/10 rounded-lg text-sm text-white focus:outline-none focus:border-[#009b3a]/40" />
                </div>
                <div>
                  <label className="block text-xs text-gray-400 mb-1">{t('saCompanies.field_email')}</label>
                  <input type="email" value={formData.email} onChange={e => setFormData(p => ({...p, email: e.target.value}))} className="w-full px-3 py-2.5 bg-[#0a0f1c] border border-white/10 rounded-lg text-sm text-white focus:outline-none focus:border-[#009b3a]/40" data-testid="input-company-email" />
                </div>
                <div>
                  <label className="block text-xs text-gray-400 mb-1">{t('saCompanies.field_phone')}</label>
                  <input value={formData.phone} onChange={e => setFormData(p => ({...p, phone: e.target.value}))} className="w-full px-3 py-2.5 bg-[#0a0f1c] border border-white/10 rounded-lg text-sm text-white focus:outline-none focus:border-[#009b3a]/40" />
                </div>
                <div>
                  <label className="block text-xs text-gray-400 mb-1">SIRET</label>
                  <input value={formData.siret} onChange={e => setFormData(p => ({...p, siret: e.target.value}))} className="w-full px-3 py-2.5 bg-[#0a0f1c] border border-white/10 rounded-lg text-sm text-white focus:outline-none focus:border-[#009b3a]/40" />
                </div>
                <div className="col-span-2">
                  <label className="block text-xs text-gray-400 mb-1">{t('saCompanies.field_address')}</label>
                  <input value={formData.address} onChange={e => setFormData(p => ({...p, address: e.target.value}))} className="w-full px-3 py-2.5 bg-[#0a0f1c] border border-white/10 rounded-lg text-sm text-white focus:outline-none focus:border-[#009b3a]/40" />
                </div>
                <div>
                  <label className="block text-xs text-gray-400 mb-1">{t('saCompanies.field_plan')}</label>
                  <select value={formData.subscription_plan} onChange={e => setFormData(p => ({...p, subscription_plan: e.target.value}))} className="w-full px-3 py-2.5 bg-[#0a0f1c] border border-white/10 rounded-lg text-sm text-white focus:outline-none" data-testid="select-plan">
                    <option value="start">{t('saCompanies.plan_option_start', { price: formatCurrency(29.99) })}</option>
                    <option value="pro">{t('saCompanies.plan_option_pro', { price: formatCurrency(59.99) })}</option>
                    <option value="super">{t('saCompanies.plan_option_super', { price: formatCurrency(99.99) })}</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs text-gray-400 mb-1">{t('saCompanies.field_shop_count')}</label>
                  <input type="number" min="1" value={formData.shop_count} onChange={e => setFormData(p => ({...p, shop_count: parseInt(e.target.value) || 1}))} className="w-full px-3 py-2.5 bg-[#0a0f1c] border border-white/10 rounded-lg text-sm text-white focus:outline-none focus:border-[#009b3a]/40" />
                </div>
              </div>

              <div className="border-t border-white/5 pt-4 mt-4">
                <h3 className="text-xs text-gray-400 font-semibold mb-3 uppercase tracking-wider">{t('saCompanies.admin_account')}</h3>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs text-gray-400 mb-1">{t('saCompanies.field_admin_email')}</label>
                    <input type="email" value={formData.admin_email} onChange={e => setFormData(p => ({...p, admin_email: e.target.value}))} className="w-full px-3 py-2.5 bg-[#0a0f1c] border border-white/10 rounded-lg text-sm text-white focus:outline-none focus:border-[#009b3a]/40" data-testid="input-admin-email" />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-400 mb-1">{t('saCompanies.field_password')}</label>
                    <input type="password" value={formData.admin_password} onChange={e => setFormData(p => ({...p, admin_password: e.target.value}))} className="w-full px-3 py-2.5 bg-[#0a0f1c] border border-white/10 rounded-lg text-sm text-white focus:outline-none focus:border-[#009b3a]/40" data-testid="input-admin-password" />
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-xs text-gray-400 mb-1">{t('saCompanies.field_notes')}</label>
                <textarea value={formData.notes} onChange={e => setFormData(p => ({...p, notes: e.target.value}))} rows={2} className="w-full px-3 py-2.5 bg-[#0a0f1c] border border-white/10 rounded-lg text-sm text-white focus:outline-none focus:border-[#009b3a]/40 resize-none" />
              </div>

              <div className="flex justify-end gap-3 pt-2">
                <button type="button" onClick={() => setShowCreate(false)} className="px-4 py-2.5 text-sm text-gray-400 hover:text-white transition-colors">{t('saCompanies.cancel')}</button>
                <button type="submit" data-testid="submit-create-company" className="px-5 py-2.5 bg-[#009b3a] text-white rounded-lg text-sm font-medium hover:bg-[#00b344] transition-colors">{t('saCompanies.create_company')}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Detail Modal */}
      {selectedCompany && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4">
          <div className="bg-[#111827] border border-white/10 rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="company-detail-modal">
            <div className="flex items-center justify-between px-5 py-4 border-b border-white/5">
              <h2 className="text-base font-bold text-white">{selectedCompany.company?.name}</h2>
              <button onClick={() => setSelectedCompany(null)} className="p-1 rounded-lg hover:bg-white/5">
                <X className="w-5 h-5 text-gray-400" />
              </button>
            </div>
            <div className="p-5 space-y-4">
              {/* Company Info */}
              <div className="grid grid-cols-2 gap-4">
                <div className="flex items-center gap-2 text-sm">
                  <Mail className="w-4 h-4 text-gray-500" />
                  <span className="text-gray-300">{selectedCompany.company?.email || '-'}</span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <Phone className="w-4 h-4 text-gray-500" />
                  <span className="text-gray-300">{selectedCompany.company?.phone || '-'}</span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <MapPin className="w-4 h-4 text-gray-500" />
                  <span className="text-gray-300">{selectedCompany.company?.address || '-'}</span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <Users className="w-4 h-4 text-gray-500" />
                  <span className="text-gray-300">{t('saCompanies.users_count', { count: formatNumber(selectedCompany.user_count) })}</span>
                </div>
              </div>

              {/* Subscription */}
              {selectedCompany.subscription && (
                <div className="bg-[#0a0f1c] rounded-xl p-4 border border-white/5">
                  <h3 className="text-xs text-gray-500 uppercase tracking-wider mb-3">{t('saCompanies.subscription')}</h3>
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <p className="text-xs text-gray-500">{t('saCompanies.col_plan')}</p>
                      <p className={`text-sm font-bold uppercase ${PLAN_COLORS[selectedCompany.subscription.plan]}`}>{selectedCompany.subscription.plan_name}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">{t('saCompanies.monthly_price')}</p>
                      <p className="text-sm font-bold text-white">{formatCurrency(selectedCompany.subscription.monthly_price)}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">{t('saCompanies.next_billing')}</p>
                      <p className="text-sm text-gray-300">{selectedCompany.subscription.next_billing_date ? formatDate(selectedCompany.subscription.next_billing_date) : '-'}</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Users */}
              <div>
                <h3 className="text-xs text-gray-500 uppercase tracking-wider mb-2">{t('saCompanies.col_users')}</h3>
                <div className="space-y-1">
                  {(selectedCompany.users || []).map(u => (
                    <div key={u.id} className="flex items-center justify-between px-3 py-2 bg-[#0a0f1c] rounded-lg">
                      <div>
                        <p className="text-sm text-white">{u.username}</p>
                        <p className="text-xs text-gray-500">{u.role} - {u.email}</p>
                      </div>
                    </div>
                  ))}
                  {(selectedCompany.users || []).length === 0 && (
                    <p className="text-xs text-gray-600 py-2">{t('saCompanies.no_users')}</p>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </SuperAdminLayout>
  );
};

export default SuperAdminCompanies;
